import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../utils/api';
import { Order } from '../types';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Button,
  Stack,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { reorderFromPrevious } from '../utils/api';
import { useCart } from '../context/CartContext';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const fetchOrders = async (): Promise<Order[]> => {
  const { data } = await api.get('/orders');
  // Backend returns paginated response { results: Order[], total: number }
  return data.results || data;
};

const OrdersPage: React.FC = () => {
	const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { data: orders, isLoading, isError, error } = useQuery<Order[], Error>({
    queryKey: ['orders'],
    queryFn: fetchOrders,
  });

  const handleReorder = async (orderId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      const response = await reorderFromPrevious(orderId);
      
      console.log('Reorder response:', response);
      
      if (!response.cart_items || response.cart_items.length === 0) {
        toast.error('No items available to reorder');
        return;
      }
      
      // Add each cart item to the cart sequentially
      let addedCount = 0;
      for (const cartItem of response.cart_items) {
        try {
          console.log('Adding to cart:', cartItem);
          await addToCart(cartItem.productId, cartItem.caseQuantity, cartItem.numberOfCases, false);
          addedCount++;
        } catch (error) {
          console.error('Failed to add item to cart:', cartItem, error);
          toast.error(`Failed to add ${cartItem.product?.name || 'item'} to cart`);
        }
      }
      
      if (addedCount > 0) {
        toast.success(`Added ${addedCount} items to cart from order`);
        
        if (response.unavailable_items && response.unavailable_items.length > 0) {
          toast.error(`Some items were unavailable: ${response.unavailable_items.join(', ')}`);
        }
        
        // Navigate to cart
        navigate('/cart');
      } else {
        toast.error('Failed to add any items to cart');
      }
    } catch (error) {
      console.error('Reorder failed:', error);
      toast.error('Failed to reorder items');
    }
  };

  if (isLoading) return <CircularProgress />;
  if (isError) return <Alert severity="error">Error fetching orders: {error.message}</Alert>;

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
        Your Orders
      </Typography>
      {orders && orders.length > 0 ? (
        <List sx={{ bgcolor: 'background.paper', borderRadius: 2 }}>
          {orders.map((order: Order, index: number) => {
            const itemCount = order.order_items.reduce((acc, item) => acc + item.quantity, 0);
            return (
              <React.Fragment key={order.id}>
                <ListItem 
                  alignItems="flex-start" 
                  sx={{ 
                    py: 2, 
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                  }}
                >
                  <ListItemText
                    primary={`Order #${order.order_number}`}
                    secondary={
                      <Box component="span" sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
                        <Typography component="span" variant="body2">
                          {format(new Date(order.created_at), 'PPP')}
                        </Typography>
                        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip label={order.status} size="small" />
                          {isAdmin && order.trusted_contact_id && (
                            <Chip 
                              label="Has Customer Contact" 
                              size="small" 
                              variant="outlined"
                              color="primary"
                            />
                          )}
                          {isAdmin && order.contact_preference && (
                            <Chip 
                              label="Contact Requested" 
                              size="small" 
                              variant="outlined"
                              color="success"
                            />
                          )}
                        </Box>
                      </Box>
                    }
                  />
                  <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                    <Typography variant="body1">{itemCount} item(s)</Typography>
                    <Typography variant="h6" fontWeight="bold">
                      ${(order.total_cents / 100).toFixed(2)}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={() => navigate(`/orders/${order.id}`)}
                      >
                        View Details
                      </Button>
                      <Button 
                        variant="contained" 
                        size="small" 
                        onClick={(e) => handleReorder(order.id, e)}
                        sx={{ 
                          bgcolor: 'primary.main',
                          '&:hover': { bgcolor: 'primary.dark' }
                        }}
                      >
                        Reorder
                      </Button>
                    </Stack>
                  </Box>
                </ListItem>
                {index < orders.length - 1 && <Divider component="li" />}
              </React.Fragment>
            )
          })}
        </List>
      ) : (
        <Typography>You have no orders yet.</Typography>
      )}
    </Box>
  );
};

export default OrdersPage; 